import { decodeJwt } from 'jose';
import { supabase } from '@/integrations/supabase/client';
import type { ExpiredJwtReplayRequest } from '@/integrations/supabase/expiredJwtReplayFetch';
import {
  type AuthRecoveryOutcome,
  type ProtectedFunctionAuthRecovery,
  recordStaleTokenRecovered,
} from '@/lib/authRecoveryTelemetry';
import { FunctionInvokeError } from '@/lib/functionInvokeErrors';
import { isClientSessionStored } from '@/lib/authLibrarySignOut';
import { classifyAuthError } from '@/lib/inviteFailureClass';
import {
  adoptSessionEndedFromOtherTab,
  clearSessionEnded,
  forgetSessionEndedAcrossTabs,
  isSessionEnded,
  isVoluntarySignOutOf,
  markSessionEnded,
} from '@/lib/sessionEnded';

export type { AuthRecoveryOutcome, ProtectedFunctionAuthRecovery } from '@/lib/authRecoveryTelemetry';

const AUTH_SESSION_ATTEMPT_DELAYS_MS = [0, 50, 150, 300, 600];

interface ProtectedFunctionAuthOptions {
  expectedUserId?: string;
  accessToken?: string;
  sessionUserId?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  signal?: AbortSignal;
}

type FunctionInvokeResult<TResponse> = Awaited<
  ReturnType<typeof supabase.functions.invoke<TResponse>>
>;

/** The invoke result plus the ENT-5492 recovery contract when a 401 was handled. */
export type ProtectedFunctionInvokeResult<TResponse> = FunctionInvokeResult<TResponse> & {
  authRecovery?: ProtectedFunctionAuthRecovery;
};

async function delay(ms: number) {
  if (ms <= 0) {
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, ms));
}

export interface SessionExpiryContext {
  functionName: string;
  error?: unknown;
  recovery: ProtectedFunctionAuthRecovery;
}

export type SessionExpiryHandler = (context: SessionExpiryContext) => void;

let sessionExpiryHandler: SessionExpiryHandler | null = null;

/**
 * Register the app-level handler invoked when a protected function call is
 * TERMINALLY unauthenticated (a 401 whose refresh of the stored session
 * failed). The handler is the ONLY owner of involuntary sign-out (ENT-5492 D1):
 * library sign-out, app-key cleanup, the in-place /auth notice and Sentry.
 * Registering resets the once-guard. ENT-2371.
 */
export function setSessionExpiryHandler(handler: SessionExpiryHandler | null): void {
  sessionExpiryHandler = handler;
  clearSessionEnded();
  deferredLibrarySignOut = false;
}

/**
 * Fire the registered session-expiry handler at most once per dead session, so
 * a burst of concurrent terminal 401s triggers ONE recovery, not one per call.
 * The guard (the shared `sessionEnded` latch) is reset whenever a refresh later
 * succeeds. Returns whether this call fired the handler.
 */
function notifySessionExpiry(context: SessionExpiryContext): boolean {
  if (!markSessionEnded()) {
    return false;
  }

  try {
    sessionExpiryHandler?.(context);
  } catch (handlerError) {
    console.error('Session expiry handler failed:', handlerError);
  }
  return true;
}

/** `functionName` of a session the auth library ended on its own. */
export const AUTH_LIBRARY_FUNCTION_NAME = 'auth-library';

/**
 * ENT-5492 AC5/AC6: the auth library emitted SIGNED_OUT. When it signed a dead
 * session out on its own (`getUser` → `session_not_found` removes the stored
 * session before any protected call can 401 and refresh), this is the only
 * signal, so it ends the session through the same once-guarded handler: one
 * notice, one Sentry warning. Not when another tab published the involuntary
 * reason (adopted instead), after an app-initiated sign-out, or while a
 * protected call's refresh is in flight (that recovery owns the outcome and
 * reports the real function). `endedSessionId` is the `session_id` of the
 * session this tab last held. Returns whether this call ended the session.
 */
export function endSessionSignedOutByAuthLibrary(endedSessionId: string | null): boolean {
  if (adoptSessionEndedFromOtherTab() || isVoluntarySignOutOf(endedSessionId)) {
    return false;
  }
  if (inFlightRefresh) {
    // Deferred, never dropped (hardening F1): the recovery can exit without an
    // ending (a 500 auth-js treats as fatal, a session-missing refresh), so
    // once the refresh settles the library path ends it if nothing else did.
    deferredLibrarySignOut = true;
    return false;
  }

  return endLibrarySignedOutSession();
}

let deferredLibrarySignOut = false;

/** After the deferred-to refresh settled: end it unless that recovery did, or a session is back. */
function settleDeferredLibrarySignOut(): void {
  if (!deferredLibrarySignOut) {
    return;
  }
  deferredLibrarySignOut = false;
  // Codex round 2 P2: THIS client's key only, never any `sb-*-auth-token`.
  if (!isSessionEnded() && !isClientSessionStored()) {
    endLibrarySignedOutSession();
  }
}

function endLibrarySignedOutSession(): boolean {
  return notifySessionExpiry({
    functionName: AUTH_LIBRARY_FUNCTION_NAME,
    recovery: {
      outcome: 'terminal',
      functionName: AUTH_LIBRARY_FUNCTION_NAME,
      errorCode: null,
      requestId: null,
      tokenAgeSeconds: null,
      storedSessionMatched: false,
      refreshAttempted: false,
      terminalReason: 'signed_out_by_auth_library',
    },
  });
}

/**
 * Re-arm the once-guard so a future dead session can trigger recovery again.
 * Call this whenever a valid session is (re-)established — after a successful
 * refresh, or on a fresh sign-in — otherwise a terminal failure would latch the
 * guard and suppress recovery for the rest of the app's lifetime (ENT-2371,
 * Codex round-1 P2: recovery routes via SPA navigation, not a full reload, so
 * module state survives the re-login).
 */
export function resetSessionExpiryGuard(): void {
  clearSessionEnded();
  forgetSessionEndedAcrossTabs();
}

// Coalesces concurrent refresh attempts into a single in-flight request. A
// burst of protected invokes that all 401 at token-expiry would otherwise each
// call refreshSession() and race the refresh-token rotation, invalidating the
// token for the others (the ENT-2371 production incident). One shared refresh
// removes the race.
let inFlightRefresh: ReturnType<typeof supabase.auth.refreshSession> | null = null;

function refreshSessionOnce(): ReturnType<typeof supabase.auth.refreshSession> {
  if (!inFlightRefresh) {
    const refresh = supabase.auth.refreshSession();
    inFlightRefresh = refresh;
    void Promise.resolve(refresh).finally(() => {
      if (inFlightRefresh === refresh) {
        inFlightRefresh = null;
      }
      // A macrotask: every caller's recovery of this refresh has run by then.
      if (deferredLibrarySignOut) {
        setTimeout(settleDeferredLibrarySignOut, 0);
      }
    });
  }

  return inFlightRefresh;
}

export async function getProtectedFunctionHeaders(
  functionName: string,
  options: ProtectedFunctionAuthOptions = {},
) {
  const providedAccessToken = options.accessToken?.trim();
  if (providedAccessToken) {
    if (options.expectedUserId && options.sessionUserId && options.sessionUserId !== options.expectedUserId) {
      throw new FunctionInvokeError('User not authenticated', {
        functionName,
        errorClass: 'authentication_error',
        errorCode: 'auth_invalid',
        status: 401,
      });
    }

    return {
      Authorization: `Bearer ${providedAccessToken}`,
    };
  }

  for (const delayMs of AUTH_SESSION_ATTEMPT_DELAYS_MS) {
    await delay(delayMs);

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token?.trim();
    const sessionUserId = session?.user?.id ?? null;

    if (!accessToken) {
      continue;
    }

    if (options.expectedUserId && sessionUserId !== options.expectedUserId) {
      continue;
    }

    return {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  throw new FunctionInvokeError('User not authenticated', {
    functionName,
    errorClass: 'authentication_error',
    errorCode: 'auth_invalid',
    status: 401,
  });
}

function isAuthFailureResponse(response: Response | undefined): boolean {
  return response?.status === 401;
}

function getInvokeErrorResponse(error: unknown): Response | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const context = (error as { context?: unknown }).context;
  return context instanceof Response ? context : undefined;
}

function isAuthFailureError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error && error.status === 401) {
    return true;
  }

  if ('message' in error && typeof error.message === 'string') {
    return /invalid\s+(jwt|token)|user not authenticated|not authenticated/i.test(error.message);
  }

  return false;
}

function isRecoverableAuthInvokeFailure(result: FunctionInvokeResult<unknown>): boolean {
  if (!result.error) {
    return false;
  }

  return isAuthFailureResponse(result.response) ||
    isAuthFailureResponse(getInvokeErrorResponse(result.error)) ||
    isAuthFailureError(result.error);
}

function readTokenClaims(accessToken: string): { sub: string | null; iat: number | null } {
  try {
    const { sub, iat } = decodeJwt(accessToken);
    return {
      sub: typeof sub === 'string' && sub ? sub : null,
      iat: typeof iat === 'number' && Number.isFinite(iat) ? iat : null,
    };
  } catch {
    return { sub: null, iat: null };
  }
}

interface AuthFailureDetails {
  errorCode: string | null;
  requestId: string | null;
}

async function readAuthFailureDetails(response: Response | undefined): Promise<AuthFailureDetails> {
  if (!response) {
    return { errorCode: null, requestId: null };
  }

  const headerRequestId = typeof response.headers?.get === 'function'
    ? response.headers.get('sb-request-id')
    : null;
  try {
    const json = await response.clone().json() as Record<string, unknown>;
    const errorCode = typeof json.errorCode === 'string' ? json.errorCode : typeof json.code === 'string' ? json.code : null;
    const requestId = typeof json.requestId === 'string' ? json.requestId : null;
    return { errorCode, requestId: requestId ?? headerRequestId };
  } catch {
    return { errorCode: null, requestId: headerRequestId };
  }
}

/**
 * Classify an auth library (supabase-js auth-js) error by its class name. By
 * name, not `instanceof`, so a duplicated bundle of the library still matches.
 */
function isAuthLibraryError(
  error: unknown,
  name: 'AuthRetryableFetchError' | 'AuthRefreshDiscardedError' | 'AuthSessionMissingError',
): boolean {
  return Boolean(error && typeof error === 'object' && (error as { name?: unknown }).name === name);
}

/**
 * Reads the stored session. Reading refreshes an expiring session inline; when
 * that refresh is discarded because another tab rotated the session first
 * (the client has no cross-tab lock), the rotated session now in storage is the
 * live one, so read it once more instead of treating the discard as an end.
 */
async function readStoredSession() {
  const firstRead = await supabase.auth.getSession();
  if (firstRead.data.session || !isAuthLibraryError(firstRead.error, 'AuthRefreshDiscardedError')) {
    return firstRead;
  }
  return supabase.auth.getSession();
}

/** D4 terminal reason: auth library error code → HTTP status → "unknown". */
function describeTerminalReason(error: unknown, fallbackStatus?: number): string {
  if (error && typeof error === 'object') {
    const { code, status } = error as { code?: unknown; status?: unknown };
    if (typeof code === 'string' && code) {
      return code;
    }
    if (typeof status === 'number') {
      return String(status);
    }
  }
  return fallbackStatus === undefined ? 'unknown' : String(fallbackStatus);
}

/** A request the server rejected as unauthenticated, and how to replay it. */
interface RejectedRequest<TResult> {
  /** Names the request in the recovery contract: a function name, or `rest:<path>`. */
  label: string;
  initialResult: TResult;
  rejectedToken: string;
  expectedUserId?: string;
  replay: (accessToken: string) => Promise<TResult>;
  isAuthFailure: (result: TResult) => boolean;
  responseOf: (result: TResult) => Response | undefined;
  errorOf: (result: TResult) => unknown;
  /** Default true. False hands a replay still rejected after a refresh back untouched. */
  endSessionIfReplayRejected?: boolean;
}

interface RecoveredRequest<TResult> {
  result: TResult;
  /** Absent when the rejection was handed back untouched (another identity, or transient). */
  recovery?: ProtectedFunctionAuthRecovery;
}

/**
 * The ONE stale-token recovery for every request the client sends with the
 * session's access token: protected Edge Function invokes and (ENT-6272)
 * direct PostgREST reads. ENT-5492 (D1): a 401 is a question, never a verdict.
 * Recovery, bounded and always on the CURRENTLY STORED session:
 *   1. the rejected token is not the stored session's token → replay once with
 *      the stored session (`retried_with_stored_session`);
 *   2. otherwise, or if that replay is rejected too → refresh the stored
 *      session once (shared by every concurrent caller) and replay once
 *      (`refreshed_and_retried`);
 *   3. only a failed refresh (or a refreshed replay still rejected) is
 *      terminal, and only the unified session-expiry handler acts on it, once.
 * A rejected token of another user, or a stored session that is not the
 * caller's `expectedUserId`, is a hard 401: never replayed, never terminal.
 */
async function recoverRejectedRequest<TResult>(
  request: RejectedRequest<TResult>,
): Promise<RecoveredRequest<TResult>> {
  const { label, initialResult, rejectedToken, expectedUserId, replay, isAuthFailure } = request;
  const rejectedClaims = readTokenClaims(rejectedToken);
  const { data: { session: storedSession }, error: storedReadError } = await readStoredSession();
  const storedToken = storedSession?.access_token?.trim() ?? null;
  const storedUserId = storedSession?.user?.id ?? null;

  if (storedSession && (
    (expectedUserId && storedUserId !== expectedUserId)
    || (rejectedClaims.sub && storedUserId !== rejectedClaims.sub)
  )) {
    // The stored session belongs to someone else: the rejected request was made
    // for another identity. Never replay it under the stored user's session.
    return { result: initialResult };
  }

  const failure = await readAuthFailureDetails(request.responseOf(initialResult));
  const storedSessionMatched = storedToken === rejectedToken;
  const buildRecovery = (
    outcome: AuthRecoveryOutcome,
    refreshAttempted: boolean,
    terminalReason: string | null = null,
  ): ProtectedFunctionAuthRecovery => ({
    outcome,
    functionName: label,
    errorCode: failure.errorCode,
    requestId: failure.requestId,
    tokenAgeSeconds: rejectedClaims.iat === null
      ? null
      : Math.max(0, Math.round(Date.now() / 1000 - rejectedClaims.iat)),
    storedSessionMatched,
    refreshAttempted,
    terminalReason,
  });
  const recovered = (result: TResult, recovery: ProtectedFunctionAuthRecovery): RecoveredRequest<TResult> => {
    recordStaleTokenRecovered(recovery);
    return { result, recovery };
  };
  const terminal = (result: TResult, terminalReason: string): RecoveredRequest<TResult> => {
    const recovery = buildRecovery('terminal', true, terminalReason);
    // Terminal: route through the unified session-expiry handler (once)
    // instead of returning a raw 401 for each caller to surface its own toast.
    const notified = notifySessionExpiry({ functionName: label, error: request.errorOf(result), recovery });
    return {
      result,
      recovery: notified ? recovery : { ...recovery, outcome: 'terminal_duplicate_suppressed' },
    };
  };

  const alreadySignedOut = (result: TResult): RecoveredRequest<TResult> => {
    // Nothing is stored: the session was already signed out — by another tab's
    // unified handler (broadcast through the auth library), or voluntarily.
    // That sign-out has its owner; this tab only goes quiet. Never a second
    // handler run or Sentry warning (D4). The session-ended notice is adopted
    // only when that owner published an involuntary reason: a request caught
    // in flight by a voluntary sign-out must never claim "your session ended".
    adoptSessionEndedFromOtherTab();
    return {
      result,
      recovery: buildRecovery('terminal_duplicate_suppressed', false, 'session_missing'),
    };
  };

  if (!storedSession) {
    if (!storedReadError) {
      return alreadySignedOut(initialResult);
    }
    // Reading the stored session made the library refresh an expired one.
    if (classifyAuthError(storedReadError) === 'transient'
      || isAuthLibraryError(storedReadError, 'AuthRefreshDiscardedError')) {
      // Network / 5xx, or a refresh discarded twice in a row: the library
      // keeps its session. Transient, never an end.
      return { result: initialResult };
    }
    // The refresh token is dead and the library has just removed its session:
    // this IS the first involuntary sign-out, so it is terminal and observable.
    return terminal(initialResult, describeTerminalReason(storedReadError));
  }

  let lastResult = initialResult;
  if (storedToken && !storedSessionMatched) {
    const storedResult = await replay(storedToken);
    if (!isAuthFailure(storedResult)) {
      return recovered(storedResult, buildRecovery('retried_with_stored_session', false));
    }
    lastResult = storedResult;
  }

  const refreshResult = await refreshSessionOnce();
  let refreshedSession = refreshResult.data.session;
  let refreshError: unknown = refreshResult.error;

  if (refreshError && isAuthLibraryError(refreshError, 'AuthRefreshDiscardedError')) {
    // Storage changed under our refresh: another tab (the client has no
    // cross-tab lock) or a concurrent call rotated the session first. The
    // stored session is the live one — use it, never treat this as an end.
    const { data: { session: rotatedSession }, error: rotatedReadError } = await readStoredSession();
    if (!rotatedSession) {
      // Same taxonomy as the first stored read: only a clean empty read means
      // signed out; a re-read still racing (discarded) or offline is transient.
      return rotatedReadError ? { result: lastResult } : alreadySignedOut(lastResult);
    }
    if (rotatedSession.user?.id !== storedUserId) {
      // Another tab signed a different user in meanwhile: never replay this
      // request under their session.
      return { result: lastResult };
    }
    refreshedSession = rotatedSession;
    refreshError = null;
  }

  if (refreshError && isAuthLibraryError(refreshError, 'AuthSessionMissingError')) {
    // The session was removed between our read and the refresh (another tab
    // signed out): an existing sign-out with its own owner, never an
    // involuntary one of ours.
    return alreadySignedOut(lastResult);
  }

  if (refreshError && classifyAuthError(refreshError) === 'transient') {
    // Network, 408/425/429 or 5xx (or no status at all): the auth service gave
    // no verdict on the refresh token. The library keeps its session; only an
    // authoritative refresh failure is terminal (D1).
    return { result: lastResult };
  }

  const refreshedToken = refreshedSession?.access_token?.trim();

  if (refreshError || !refreshedToken) {
    return terminal(lastResult, describeTerminalReason(refreshError));
  }

  if (
    refreshedSession?.user?.id !== storedUserId
    || (expectedUserId && refreshedSession?.user?.id !== expectedUserId)
  ) {
    // The account changed between reading the stored session and refreshing
    // it: never replay this request under another identity.
    return { result: lastResult };
  }

  const retriedResult = await replay(refreshedToken);

  // If the replay STILL fails auth, the refresh did not actually recover the
  // session (e.g. the account/token is genuinely rejected). Route through the
  // same terminal recovery rather than returning a second 401 for callers to
  // toast (ENT-2371 Codex round-2 P2).
  if (isAuthFailure(retriedResult)) {
    if (request.endSessionIfReplayRejected === false) {
      // The first rejection was never proven an expiry (ENT-6272: a bodyless
      // HEAD), so a fresh token that still fails is not a dead session.
      return { result: retriedResult };
    }
    return terminal(retriedResult, describeTerminalReason(null, request.responseOf(retriedResult)?.status ?? 401));
  }

  // Only a replay that succeeded proves the session healthy again: re-arm the
  // once-guard for a future independent expiry now, never before. And never
  // over an overlapping recovery that already went terminal: its sign-out is
  // under way, and its notice and cross-tab reason must survive.
  if (!isSessionEnded()) {
    resetSessionExpiryGuard();
  }
  return recovered(retriedResult, buildRecovery('refreshed_and_retried', true));
}

/**
 * ENT-6272: a direct PostgREST request that the server rejected as
 * `401 PGRST303 "JWT expired"` goes through the same recovery as a protected
 * Edge Function: one shared refresh, one replay, the ENT-5492 dead-session path
 * when the refresh fails. The Supabase client's fetch
 * (`@/integrations/supabase/expiredJwtReplayFetch`) decides which requests are
 * safe to replay; this only recovers them.
 */
export async function recoverExpiredRestRequest(request: ExpiredJwtReplayRequest): Promise<Response> {
  const { result } = await recoverRejectedRequest<Response>({
    label: request.label,
    initialResult: request.rejected,
    rejectedToken: request.rejectedToken,
    replay: request.replay,
    isAuthFailure: isAuthFailureResponse,
    responseOf: (response) => response,
    errorOf: () => undefined,
    endSessionIfReplayRejected: request.endSessionIfReplayRejected,
  });
  return result;
}

/**
 * Invoke a protected Edge Function with the caller's (or the stored) session.
 *
 * A 401 goes through `recoverRejectedRequest` — for the implicit path AND for
 * callers that pass their own `accessToken`, which may be stale (a pre-refresh
 * token held by a closure, or a token of an earlier session in the same
 * browser).
 */
export async function invokeProtectedFunction<TResponse>(
  functionName: string,
  body: Record<string, unknown> | undefined,
  options: ProtectedFunctionAuthOptions = {},
): Promise<ProtectedFunctionInvokeResult<TResponse>> {
  const invokeWithHeaders = async (headers: Record<string, string>) =>
    // orchestration-invoke-allowlist-dynamic-ok: generic protected-function DI
    // helper; concrete function names are literal + allowlisted at the call sites.
    supabase.functions.invoke<TResponse>(functionName, {
      ...(body === undefined ? {} : { body }),
      headers,
      ...(options.method ? { method: options.method } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

  const initialHeaders = await getProtectedFunctionHeaders(functionName, options);
  const initialResult = await invokeWithHeaders(initialHeaders);

  if (!isRecoverableAuthInvokeFailure(initialResult)) {
    return initialResult;
  }

  const { result, recovery } = await recoverRejectedRequest<FunctionInvokeResult<TResponse>>({
    label: functionName,
    initialResult,
    rejectedToken: initialHeaders.Authorization.slice('Bearer '.length),
    expectedUserId: options.expectedUserId,
    replay: (accessToken) => invokeWithHeaders({ Authorization: `Bearer ${accessToken}` }),
    isAuthFailure: isRecoverableAuthInvokeFailure,
    responseOf: (invokeResult) => invokeResult.response ?? getInvokeErrorResponse(invokeResult.error),
    errorOf: (invokeResult) => invokeResult.error,
  });
  return recovery ? { ...result, authRecovery: recovery } : result;
}
